// "My signature": a member's saved signature and initials. They're offered when signing after signing in
// (their own envelopes, or anything their company sends them). Stored per account, not per envelope.
import { useEffect, useState } from "react";
import { PenLine, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useMe } from "../App";
import { SignaturePad } from "../components/SignaturePad";
import { Button, ErrorNote, Spinner, toast } from "../components/ui";

type Kind = "signature" | "initials";
type Saved = Partial<Record<Kind, string>>;

export default function MySignature() {
  const me = useMe();
  const [saved, setSaved] = useState<Saved | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Kind | null>(null);
  const [busy, setBusy] = useState<Kind | null>(null);

  useEffect(() => {
    api<Saved>("/me/signature").then(setSaved, (e) => setError((e as Error).message));
  }, []);

  const save = async (kind: Kind, png: string) => {
    setBusy(kind);
    try {
      setSaved(await api<Saved>("/me/signature", { body: { [kind]: png } }));
      toast(kind === "signature" ? "Signature saved" : "Initials saved");
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };
  const remove = async (kind: Kind) => {
    setBusy(kind);
    try {
      setSaved(await api<Saved>(`/me/signature?kind=${kind}`, { method: "DELETE" }));
      toast(kind === "signature" ? "Signature deleted" : "Initials deleted");
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <h1 className="text-xl font-semibold flex items-center gap-2 mb-1">
        <PenLine className="size-5 text-accent" /> My signature
      </h1>
      <p className="text-sm text-muted mb-6">
        Saved here, your signature and initials are offered automatically whenever you sign as a signed-in member of your company. You can still type, draw or upload a different one each time.
      </p>
      {error ? (
        <ErrorNote>{error}</ErrorNote>
      ) : !saved ? (
        <Spinner />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {(["signature", "initials"] as Kind[]).map((kind) => (
            <section key={kind} className="bg-surface border border-line rounded-2xl p-5">
              <h2 className="text-sm font-semibold mb-3">{kind === "signature" ? "Signature" : "Initials"}</h2>
              <div className="rounded-lg border border-line bg-white h-28 flex items-center justify-center px-4 overflow-hidden">
                {saved[kind] ? <img src={saved[kind]} alt={`Your saved ${kind}`} className="max-h-24 max-w-full object-contain" /> : <span className="text-sm text-muted">Nothing saved yet</span>}
              </div>
              <div className="flex gap-2 mt-3">
                <Button variant="primary" busy={busy === kind} onClick={() => setEditing(kind)}>
                  {saved[kind] ? "Replace" : "Add"}
                </Button>
                {saved[kind] && (
                  <Button variant="ghost" disabled={busy === kind} onClick={() => remove(kind)}>
                    <Trash2 className="size-4" /> Delete
                  </Button>
                )}
              </div>
            </section>
          ))}
        </div>
      )}
      {editing && (
        <SignaturePad
          kind={editing}
          name={me.name}
          title={editing === "initials" ? "Your initials" : "Your signature"}
          adoptLabel="Save"
          onClose={() => setEditing(null)}
          onAdopt={(png) => {
            const kind = editing;
            setEditing(null);
            void save(kind, png);
          }}
        />
      )}
    </div>
  );
}
